import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Eye, EyeOff, Loader2, ShieldAlert, Box } from "lucide-react";
import MobileDashboard from "./MobileDashboard";
import CadastrarSenhaModal from "./CadastrarSenhaModal";
import { MobilePrivacyConsent } from "@/components/MobilePrivacyConsent";
import { toast } from "sonner";
const FONT_MAIN = "'Hanken Grotesk', 'Inter', sans-serif";
const REMEMBER_KEY = "hd-appmobile-user";
const REVENDA = "Interaccess Sistemas. Versão 1.1.3";
const MAX_TENTATIVAS = 3;
const BLOQUEIO_MS = 60_000;

const inputStyle = {
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  color: "#e1e2ed",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
};
const inputClass = "w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff] focus:ring-1 focus:ring-[#b4c5ff] transition-all placeholder:text-[#c3c6d7]/70 disabled:opacity-40 disabled:cursor-not-allowed";

const fmtCpf = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};
const cpfDigits = (v) => v.replace(/\D/g, "");

const validaCpf = (d) => {
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dv = (base) => {
    let s = 0;
    for (let i = 0; i < base.length; i++) s += Number(base[i]) * (base.length + 1 - i);
    const r = (s * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dv(d.slice(0, 9)) === Number(d[9]) && dv(d.slice(0, 10)) === Number(d[10]);
};

function GlassCard({ children, testid }) {
  return (
    <div data-testid={testid}
      className="relative w-full rounded-[14px] overflow-hidden shadow-2xl"
      style={{
        background: "rgba(40, 42, 50, 0.6)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: "1px solid rgba(141, 144, 160, 0.5)",
        borderTop: "1px solid rgba(255, 255, 255, 0.1)",
      }}>
      <span className="absolute top-0 left-[6%] right-[6%] h-px pointer-events-none"
        style={{ background: "linear-gradient(90deg, transparent, rgba(178, 205, 245, 0.85), transparent)", boxShadow: "0 0 10px rgba(120, 165, 230, 0.45)", borderRadius: 999 }} />
      {children}
    </div>
  );
}

function FieldLabel({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor}
      className="block text-[11px] font-semibold uppercase tracking-wider mb-1"
      style={{ color: "#c3c6d7", fontFamily: FONT_MAIN }}>
      {children}
    </label>
  );
}

function AvisoFixo({ texto, testid }) {
  return (
    <div data-testid={testid || "appmobile-aviso"}
      className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
      style={{ background: "rgba(255, 180, 171, 0.08)", color: "#ffb4ab", border: "1px solid rgba(255, 180, 171, 0.3)" }}>
      <ShieldAlert className="h-4 w-4 shrink-0 mt-px" />
      <span>{texto}</span>
    </div>
  );
}

function MobileFooter({ condo, email }) {
  return (
    <footer data-testid="appmobile-footer" className="w-full max-w-md mx-auto px-4">
      <div className="h-px w-full" style={{ background: "rgba(255, 255, 255, 0.08)" }} />
      <div className="text-center py-4 pb-safe-footer">
        <p className="text-xs font-semibold" style={{ color: "#e1e2ed" }}>{REVENDA}</p>
        {condo && (
          <p className="text-xs mt-1 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5"
            style={{ color: "#c3c6d7" }} data-testid="appmobile-footer-condo">
            <span>{condo}</span>
            {email && (
              <a href={`mailto:${email}`} data-testid="appmobile-footer-email"
                title="Envie um e-mail para adm"
                className="font-medium hover:underline transition-colors"
                style={{ color: "#b4c5ff" }}>
                ADM E-mail
              </a>
            )}
          </p>
        )}
      </div>
    </footer>
  );
}

function foraDaValidade(m) {
  try {
    const agora = new Date();
    if (m.validade_inicial && new Date(m.validade_inicial) > agora) return true;
    if (m.validade_final && new Date(m.validade_final) < agora) return true;
    return false;
  } catch {
    return false;
  }
}

export default function MobileLoginPage() {
  const [origem, setOrigem] = useState("");
  const [emp, setEmp] = useState(null);
  const [empErro, setEmpErro] = useState("");
  const [avisoEmail, setAvisoEmail] = useState("");
  const [validandoEmp, setValidandoEmp] = useState(false);
  const [unidade, setUnidade] = useState("");
  const [cpf, setCpf] = useState("");
  const [senha, setSenha] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [aviso, setAviso] = useState("");
  const [tentativas, setTentativas] = useState(0);
  const [bloqueadoAte, setBloqueadoAte] = useState(0);
  const [agora, setAgora] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(null);
  const [solicitarOpen, setSolicitarOpen] = useState(false);
  const debounceRef = useRef(null);
  const origemValidadaRef = useRef("");
  const unidadeRef = useRef(null);
  const cpfRef = useRef(null);
  const senhaRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) {
      try {
        const d = JSON.parse(saved);
        setOrigem(d.origem || "");
        setUnidade(d.unidade || "");
        setCpf(d.cpf || "");
        if (d.senha) { try { setSenha(atob(d.senha)); } catch {} }
        setRemember(true);
        if (d.origem) setTimeout(() => validarOrigem(d.origem, true), 300);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bloqueadoAte) return;
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [bloqueadoAte]);

  const bloqueado = bloqueadoAte > agora;
  const segundosBloqueio = Math.max(0, Math.ceil((bloqueadoAte - agora) / 1000));

  const validarOrigem = async (valor, explicita = false) => {
    const code = (valor ?? origem).trim();
    if (validandoEmp) return;
    if (!code) return;
    if (code.length < 2 && !explicita) return;
    if (origemValidadaRef.current === code && (emp || empErro)) return;
    origemValidadaRef.current = code;
    setValidandoEmp(true);
    setEmpErro("");
    setAvisoEmail("");
    try {
      const r = await fetch(`${API}/public/empreendimento?origem=${encodeURIComponent(code)}`);
      const d = await r.json();
      if (!d.ok) {
        setEmp(null);
        setEmpErro(d.motivo === "origem_invalida"
          ? "Origem não encontrada. Verifique o código informado."
          : "Integração indisponível no momento. Tente novamente.");
      } else if (!d.ativo) {
        setEmp(null);
        setEmpErro("Sistema em Manutenção. Informar aviso 789.");
      } else if (!d.email_ok) {
        setEmp(null);
        setAvisoEmail("E-mail Adm Inconsistente. Informar aviso 792.");
      } else {
        setEmp(d);
        setTimeout(() => unidadeRef.current?.focus(), 150);
      }
    } catch {
      setEmp(null);
      setEmpErro("Integração indisponível no momento. Tente novamente.");
    } finally {
      setValidandoEmp(false);
    }
  };

  const trocarOrigem = (v) => {
    const code = v.toUpperCase();
    setOrigem(code);
    setEmp(null);
    setEmpErro("");
    setAvisoEmail("");
    setAviso("");
    setSucesso(null);
    setTentativas(0);
    origemValidadaRef.current = "";
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (code.trim().length >= 2) {
      debounceRef.current = setTimeout(() => validarOrigem(code), 800);
    }
  };

  const teclaOrigem = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      validarOrigem(undefined, true);
    } else if (e.key === "Tab") {
      validarOrigem(undefined, true);
    }
  };

  const trocarUnidade = (v) => {
    setUnidade(v.toUpperCase());
    setAviso("");
    setTentativas(0);
  };
  const trocarCpf = (v) => {
    setCpf(fmtCpf(v));
    setAviso("");
    setTentativas(0);
  };

  const unidadeOk = unidade.trim().length >= 4;
  const cpfInvalido = cpfDigits(cpf).length === 11 && !validaCpf(cpfDigits(cpf));
  const cpfOk = cpfDigits(cpf).length === 11 && !cpfInvalido;
  const unidadeEnabled = !!emp;
  const cpfEnabled = !!emp && unidadeOk;
  const senhaEnabled = !!emp && unidadeOk && cpfOk;

  const submit = async (e) => {
    e.preventDefault();
    if (bloqueado || !senhaEnabled || !senha) return;
    setAviso("");
    setLoading(true);
    try {
      const qs = new URLSearchParams({ origem: origem.trim(), eql: unidade.trim(), cpf: cpfDigits(cpf), senha });
      const r = await fetch(`${API}/public/moradores-login?${qs}`);
      const d = await r.json();
      if (!d.ok) {
        setAviso("Integração indisponível no momento. Tente novamente.");
      } else if (!d.encontrado) {
        setAviso("Cadastro não encontrado. Verifique a Unidade e o CPF informados.");
      } else if (!d.ativo) {
        setAviso("Login em Manutenção. Informar aviso 790.");
      } else if (foraDaValidade(d)) {
        setAviso("Login Vencido. Informar aviso 791.");
      } else if (d.senha === "erro") {
        const n = tentativas + 1;
        setTentativas(n);
        setSenha("");
        if (n >= MAX_TENTATIVAS) {
          setBloqueadoAte(Date.now() + BLOQUEIO_MS);
          setAviso("Tentativas excedidas. Tente mais tarde.");
          setTentativas(0);
        } else {
          setAviso(`Senha incorreta. Tentativa ${n} de ${MAX_TENTATIVAS}.`);
        }
      } else if (d.senha === "solicitar") {
        setSolicitarOpen(true);
      } else if (d.senha === "ok") {
        if (remember) {
          localStorage.setItem(REMEMBER_KEY, JSON.stringify({ origem: origem.trim(), unidade: unidade.trim(), cpf, senha: btoa(senha) }));
        } else {
          localStorage.removeItem(REMEMBER_KEY);
        }
        setSucesso(d);
      }
    } catch {
      setAviso("Integração indisponível no momento. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (sucesso) {
    return (
      <MobileDashboard
        tenant={emp?.tenant}
        condo={emp?.razao_social || emp?.apelido}
        unidade={unidade.trim()}
        nome={sucesso.nome}
        tipo={sucesso.tipo}
        origem={origem.trim()}
        cpf={cpfDigits(cpf)}
        onLogoff={() => { setSucesso(null); setSenha(""); }}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden"
      style={{ background: "#11131b", fontFamily: FONT_MAIN }} data-testid="appmobile-page">
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-8 w-full max-w-md mx-auto"
        style={{ paddingTop: "max(48px, env(safe-area-inset-top))" }}>
        <div className="mb-8 text-center w-full">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center justify-center gap-2" style={{ color: "#b4c5ff" }}
            data-testid="appmobile-title">
            <Box className="h-6 w-6 md:h-7 md:w-7" />
            Brave Access
          </h1>
          <p className="text-[11px] font-semibold uppercase tracking-wider mt-2" style={{ color: "#c3c6d7" }}
            data-testid="appmobile-subtitle">SGA - Controle de Acesso</p>
        </div>

        <GlassCard testid="appmobile-login-card">
          <form onSubmit={submit} className="flex flex-col gap-4 p-5 relative" data-testid="appmobile-form">
            <div className="flex flex-col gap-1">
              <FieldLabel htmlFor="origem">Origem</FieldLabel>
              <div className="relative">
                <input id="origem" type="text" data-testid="appmobile-origem-input" value={origem}
                  onChange={(e) => trocarOrigem(e.target.value)}
                  onKeyDown={teclaOrigem}
                  onBlur={() => validarOrigem(undefined, true)}
                  placeholder="Código da Origem" autoCapitalize="characters" autoCorrect="off"
                  enterKeyHint="next"
                  className={inputClass} style={inputStyle} />
                {validandoEmp && (
                  <Loader2 className="h-4 w-4 animate-spin absolute right-4 top-1/2 -translate-y-1/2"
                    style={{ color: "#8d90a0" }} data-testid="appmobile-origem-loading" />
                )}
              </div>
            </div>

            {empErro && <AvisoFixo texto={empErro} testid="appmobile-aviso-origem" />}
            {avisoEmail && <AvisoFixo texto={avisoEmail} testid="appmobile-aviso-email" />}

            <div className="flex flex-col gap-1" data-testid="appmobile-unidade-field">
              <FieldLabel htmlFor="unidade">Unidade</FieldLabel>
              <input id="unidade" type="text" data-testid="appmobile-unidade-input" value={unidade}
                ref={unidadeRef}
                onChange={(e) => trocarUnidade(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (unidadeOk) cpfRef.current?.focus(); } }}
                placeholder="Sua unidade (EQL)" autoCapitalize="characters" autoCorrect="off"
                enterKeyHint="next" disabled={!unidadeEnabled}
                className={inputClass} style={inputStyle} />
            </div>

            <div className="flex flex-col gap-1" data-testid="appmobile-login-field">
              <FieldLabel htmlFor="login">Login</FieldLabel>
              <input id="login" type="text" data-testid="appmobile-login-input" required value={cpf}
                ref={cpfRef}
                onChange={(e) => trocarCpf(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (cpfOk) senhaRef.current?.focus(); } }}
                placeholder="Informe seu CPF"
                autoCapitalize="none" autoCorrect="off" inputMode="numeric"
                enterKeyHint="next" disabled={!cpfEnabled}
                className={inputClass} style={inputStyle} />
            </div>

            {cpfInvalido && <AvisoFixo texto="CPF inválido. Verifique os números digitados." testid="appmobile-aviso-cpf" />}

            <div className="flex flex-col gap-1" data-testid="appmobile-senha-field">
              <FieldLabel htmlFor="senha">Senha</FieldLabel>
              <div className="relative">
                <input id="senha" type={showPw ? "text" : "password"} data-testid="appmobile-password-input" required
                  ref={senhaRef}
                  value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Sua senha"
                  disabled={!senhaEnabled || bloqueado} enterKeyHint="go"
                  className={`${inputClass} pr-14`} style={inputStyle} />
                <button type="button" data-testid="appmobile-toggle-password" onClick={() => setShowPw(!showPw)}
                  aria-label={showPw ? "Ocultar senha" : "Mostrar senha"}
                  className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full active:bg-white/10 transition-colors"
                  style={{ color: "#8d90a0", touchAction: "manipulation" }}>
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {aviso && <AvisoFixo texto={bloqueado ? `${aviso} (${segundosBloqueio}s)` : aviso} testid="appmobile-aviso" />}

            <div className="flex items-center justify-between mt-1">
              <Link to="/esqueci-senha" data-testid="appmobile-forgot-link"
                className="text-xs font-medium hover:underline transition-colors"
                style={{ color: "#b4c5ff" }}>
                Esqueci minha Senha
              </Link>
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-xl"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
                data-testid="appmobile-remember-label">
                <span className="text-xs" style={{ color: "#c3c6d7" }}>Lembrar Senha</span>
                <input type="checkbox" data-testid="appmobile-remember-checkbox" checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded cursor-pointer accent-[#b4c5ff]"
                  style={{ background: "rgba(255,255,255,0.1)" }} />
              </label>
            </div>

            <button type="submit" data-testid="appmobile-submit-button"
              disabled={loading || bloqueado || !senhaEnabled || !senha}
              className="mt-1 w-full py-3 rounded-xl text-base font-semibold text-white transition-all duration-300 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: "#2563EB", boxShadow: "0 0 15px rgba(37, 99, 235, 0.3)" }}>
              {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : bloqueado ? `Aguarde ${segundosBloqueio}s` : "Entrar"}
            </button>
          </form>
        </GlassCard>
      </div>
      {solicitarOpen && (
        <CadastrarSenhaModal origem={origem.trim()} eql={unidade.trim()} cpf={cpfDigits(cpf)}
          onClose={() => setSolicitarOpen(false)}
          onSuccess={() => {
            setSolicitarOpen(false);
            setSenha("");
            toast.success("Senha cadastrada com sucesso! Digite a nova senha para entrar.");
            setTimeout(() => senhaRef.current?.focus(), 150);
          }} />
      )}

      <MobilePrivacyConsent />
      <MobileFooter condo={emp?.razao_social} email={emp?.email_adm} />
    </div>
  );
}
